import Data from '@/data'
import Select from '@/ui/select'
import { Checkbox, Disclaimer, isArray, notNull } from '@maxroll-media-group/common-libs'
import React, { Dispatch, SetStateAction } from 'react'
import { buildCategories, BuildCategory, BuildTag, buildTags } from '../common'

import { ProfileEntry, UserEntry } from '@/types/api'
import { PlayerClass, Profile } from '@/types/profile'
import styles from './public-section.module.scss'

export type PublicOptions = {
  category?: BuildCategory
  tags?: BuildTag[]
}

function validateTags(tags: string) {
  try {
    const value = JSON.parse(tags)
    if (!isArray(value)) return
    return value.filter((tag): tag is BuildTag => typeof tag === 'string' && tag in buildTags)
  } catch {
    return
  }
}

export function publicFromProfile(profile: Pick<ProfileEntry, 'category' | 'tags'>, user: UserEntry | undefined) {
  const result: PublicOptions = {
    category: profile.category && profile.category in buildCategories ? (profile.category as BuildCategory) : 'endgame',
    tags: validateTags(profile.tags ?? '[]')
  }
  return result
}

export function publicName(charClass: PlayerClass, ascendancy: string | undefined, options: PublicOptions) {
  const classData = Data.characters[charClass]
  const ascendancyData = ascendancy ? classData.ascendancies[ascendancy] : undefined
  return [buildCategories[options.category || 'endgame'].name, ascendancyData?.name ?? classData.name]
    .filter(notNull)
    .join(' ')
}

export function PublicSection({
  profile,
  options,
  setOptions
}: {
  profile: Profile
  options: PublicOptions
  setOptions: Dispatch<SetStateAction<PublicOptions | undefined>>
}) {
  const categoryOptions = React.useMemo(
    () =>
      Object.entries(buildCategories).map(([id, { desc }]) => ({
        id: id as keyof typeof buildCategories,
        name: desc
      })),
    []
  )

  const onSetTag = React.useCallback(
    (tag: BuildTag) => (newValue: boolean) => {
      setOptions((options) => {
        const tags: BuildTag[] = options?.tags ? [...options.tags] : []
        const tagIndex = tags.findIndex((item) => item === tag)
        if (newValue === false && tagIndex !== -1) {
          tags.splice(tagIndex, 1)
        } else if (newValue === true && tagIndex === -1) {
          tags.push(tag)
        }

        return { ...options, tags }
      })
    },
    [setOptions]
  )

  return (
    <div className={styles.PublicSection}>
      <div className={styles.PublicSection__description}>
        <Disclaimer className={styles.PublicSection__disclaimer}>
          <span>
            Your build will be listed in the Builds tab, where other users can see and rate it. <br />
          </span>
          <span>We will unlist profiles that are not being updated regularly.</span>
        </Disclaimer>
      </div>
      <div className={styles.PublicSection__options}>
        <Select
          options={categoryOptions}
          onChange={(opt) =>
            setOptions({
              ...options,
              category: opt?.id
            })
          }
          value={
            options.category
              ? {
                  id: options.category || '',
                  name: buildCategories[options.category]?.desc ?? 'Unknown'
                }
              : undefined
          }
        />

        <Checkbox label="SSF" value={options.tags?.includes('ssf') || false} onChange={onSetTag('ssf')} />

        <Checkbox label="HC" value={options.tags?.includes('hc') || false} onChange={onSetTag('hc')} />
      </div>
    </div>
  )
}
